import React from "react"
import Link from "next/link"
import { useRouter } from "next/router"

import { Button } from "@coval/ui"
import { ExternalLink } from "lucide-react"

import { useQueryParams } from "@/hooks/useQueryParams"

interface DrawerOpenInNewTabActionProps {
  /**
   * The resource's own page, as an organization-relative path such as
   * `/test-sets/abc`. The organization segment comes from the route.
   */
  path: string
  /** Names the destination, e.g. "Open test set in new tab". */
  label: string
}

/**
 * The way out of a drawer and into the resource's own page. A drawer shows a
 * resource read-only, so without this the reader who wants to change what they
 * are looking at has to leave the page and find it again; the link opens a new
 * tab so the results they were reading stay where they were.
 *
 * Renders nothing until the route names an organization, since every resource
 * page is scoped to one and a link built without it would resolve against the
 * wrong organization.
 */
export const DrawerOpenInNewTabAction: React.FC<
  DrawerOpenInNewTabActionProps
> = ({ path, label }) => {
  const router = useRouter()
  const queryParams = useQueryParams()
  const organizationSlug =
    typeof router.query.slug === "string" ? router.query.slug : undefined

  if (!organizationSlug) {
    return null
  }

  return (
    <Button
      asChild
      variant="secondary"
      icon
      tooltip={label}
      className="shrink-0"
    >
      <Link
        href={queryParams.getHref(path, organizationSlug)}
        target="_blank"
        rel="noopener noreferrer"
      >
        <ExternalLink className="h-4 w-4" />
      </Link>
    </Button>
  )
}
